import { useState } from "react";
import PropTypes from "prop-types";

import clsx from "clsx";

// Third Party
import moment from "moment";

// MUI
import TextField from "@mui/material/TextField";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DesktopDateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Paper } from "@mui/material";

// Icons
import DateRangeRoundedIcon from "@mui/icons-material/DateRangeRounded";

// styles
import "./DateTimePicker.css";

const theme = createTheme({
	palette: {
		primary: {
			main: "#019bd9",
		},
	},
	components: {
		MuiSvgIcon: {
			styleOverrides: {
				root: {
					color: "#666666",
				},
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					"& .MuiOutlinedInput-notchedOutline": {
						borderColor: "#CCCCCC !important",
					},
				},
			},
		},
	},
});

const CustomTextField = ({ setOpen, onOpen, defaultDate, ...params }) => {
	return (
		<TextField
			{...params}
			fullWidth
			InputProps={{
				...params.InputProps,
				readOnly: true,
				onKeyDown: (e) => {
					// Prevent typing in the input field
					e.preventDefault();
				},
			}}
			onClick={() => {
				if (!params.disabled) {
					setOpen(true);
				}
				if (params?.onOpen) {
					onOpen(defaultDate);
				}
			}}
			className={clsx({
				"date-time-picker-input correct-shink-label": true,
				[params.className]: true,
			})}
			autoComplete={params?.autoComplete ? "on" : "off"}
			onContextMenu={params.onContextMenu}
		/>
	);
};

const adapter = {
	moment: AdapterMoment,
	fns: AdapterDateFns,
};

export default function DateTimePickerControlled(props) {
	const [open, setOpen] = useState(false);
	const [error, setError] = useState(false);

	const handleOnOpen = (date) => {
		if (props.onOpen) {
			props.onOpen(date);
		}
	};

	const handleChange = (date) => {
		// validate date to check if it is a valid date
		if (date && !moment(date).isValid()) {
			setError(true);
			return;
		}

		if (props.onActionUpdate && date) {
			props.onActionUpdate(date);
		}
	};

	const handleOnClose = () => {
		setOpen(false);
		if (props.onClose) {
			props.onClose();
		}
	};

	const handleAccept = (date) => {
		if (props.onAccept) {
			props.onAccept(date || null);
		}
	};

	const handleContextMenu = (event) => {
		if (props.disableContextMenu) {
			event.preventDefault();
		}
	};

	return (
		<ThemeProvider theme={theme}>
			<Paper
				elevation={0}
				className={"date-time-picker-container"}
				style={{ position: "relative", backgroundColor: "transparent", width: "100%" }}
				sx={props.sx}
			>
				<LocalizationProvider dateAdapter={adapter[props.adapter || "moment"]}>
					<DesktopDateTimePicker
						value={props.defaultDate}
						open={open}
						mask={props.mask}
						className={clsx({
							"text-input-error-date-picker": props.error || error,
						})}
						localeText={{
							toolbarTitle: props.toolbarTitle,
						}}
						views={props.views}
						viewRenderers={props.viewRenderers}
						format={props.inputFormat}
						clearable={props.clearable}
						allowSameDateSelection={props.allowSameDateSelection}
						disabled={props.disabled}
						readOnly={props.readOnly}
						disableHighlightToday={props.disableHighlightToday}
						hideTabs={props.hideTabs}
						minDateTime={props.minDateTime}
						maxDateTime={props.maxDateTime}
						minDate={props.minDate}
						maxDate={props.maxDate}
						minutesStep={props.minutesStep}
						disableOpenPicker={props.disableOpenPicker}
						orientation={props.orientation}
						cancelText={props.cancelText}
						okText={props.okText}
						todayText={props.todayText}
						disableCloseOnSelect={props.disableCloseOnSelect}
						slots={{
							openPickerIcon: DateRangeRoundedIcon,
							textField: CustomTextField,
						}}
						slotProps={{
							toolbar: {
								hidden: !props.showToolbar,
								toolbarFormat: props.toolbarFormat,
							},
							tabs: {
								hidden: props.hideTabs,
							},
							actionBar: {
								actions: props.actions,
							},
							textField: {
								setOpen,
								onOpen: props.onOpen,
								disabled: props.disabled,
								defaultDate: props.defaultDate,
								onContextMenu: handleContextMenu,
							},
						}}
						onChange={handleChange}
						onClose={handleOnClose}
						onOpen={handleOnOpen}
						onAccept={handleAccept}
					/>
				</LocalizationProvider>
			</Paper>
		</ThemeProvider>
	);
}

DateTimePickerControlled.propTypes = {
	defaultDate: PropTypes.object,
	inputFormat: PropTypes.string,
	toolbarFormat: PropTypes.string,
	mask: PropTypes.string,
	views: PropTypes.array,
	clearable: PropTypes.bool,
	allowSameDateSelection: PropTypes.bool,
	disabled: PropTypes.bool,
	readOnly: PropTypes.bool,
	disableHighlightToday: PropTypes.bool,
	hideTabs: PropTypes.bool,
	minDateTime: PropTypes.any,
	maxDateTime: PropTypes.any,
	minutesStep: PropTypes.number,
	showToolbar: PropTypes.bool,
	disableOpenPicker: PropTypes.bool,
	orientation: PropTypes.oneOf(["portrait", "landscape"]),
	toolbarTitle: PropTypes.string,
	cancelText: PropTypes.string,
	okText: PropTypes.string,
	todayText: PropTypes.string,
	viewRenderers: PropTypes.object,
	disableCloseOnSelect: PropTypes.bool,
	actions: PropTypes.array,
	onActionUpdate: PropTypes.func,
	onOpen: PropTypes.func,
	onAccept: PropTypes.func,
	error: PropTypes.bool,
	autocomplete: PropTypes.bool,
	sx: PropTypes.object,
	onClose: PropTypes.func,
	disableContextMenu: PropTypes.bool,
};
